import { useEffect, useState } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { emptyItem, emptyMenuForm } from './seasonalMenuShared';

// ─── CampaignMenuDialog ───────────────────────────────────────────────────────

const PRICE_FIELD = { type: 'number', style: { width: 70 } };
const CAL_FIELD   = { type: 'text',   style: { width: 56 } };

function PriceCell({ label, name, value, onChange }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
      <Typography sx={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{label}</Typography>
      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder="-"
        style={{
          width: 62, border: '1px solid #e0e0e0', borderRadius: 6,
          padding: '4px 6px', fontSize: 12, textAlign: 'center',
          outline: 'none', background: 'white',
        }}
      />
    </Box>
  );
}

function CampaignMenuDialog({ open, onClose, editTarget, onSubmit }) {
  const [form, setForm] = useState(emptyMenuForm);

  useEffect(() => {
    if (open) {
      if (editTarget) {
        setForm({
          name: editTarget.name || '',
          description: editTarget.description || '',
          startDate: editTarget.startDate ? editTarget.startDate.substring(0, 10) : '',
          endDate: editTarget.endDate ? editTarget.endDate.substring(0, 10) : '',
          items: (editTarget.items && editTarget.items.length > 0)
            ? editTarget.items.map(it => ({
                name:        it.name || '',
                hot12Price:  it.hot12Price  !== undefined ? String(it.hot12Price)  : (it.basePrice ? String(it.basePrice) : ''),
                hot12Cal:    it.hot12Cal    || '',
                hot16Price:  it.hot16Price  !== undefined ? String(it.hot16Price)  : '',
                hot16Cal:    it.hot16Cal    || '',
                hot20Price:  it.hot20Price  !== undefined ? String(it.hot20Price)  : '',
                hot20Cal:    it.hot20Cal    || '',
                iced16Price: it.iced16Price !== undefined ? String(it.iced16Price) : '',
                iced16Cal:   it.iced16Cal   || '',
                iced20Price: it.iced20Price !== undefined ? String(it.iced20Price) : '',
                iced20Cal:   it.iced20Cal   || '',
              }))
            : [{ ...emptyItem }],
        });
      } else {
        setForm({ ...emptyMenuForm, items: [{ ...emptyItem }] });
      }
    }
  }, [open, editTarget]);

  const handleField = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleItemField = (idx, e) => {
    const items = form.items.map((it, i) =>
      i === idx ? { ...it, [e.target.name]: e.target.value } : it
    );
    setForm(f => ({ ...f, items }));
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));

  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = () => {
    const obj = {
      ...form,
      items: form.items.map(it => ({
        name:        it.name,
        basePrice:   Number(it.hot12Price) || 0,
        hot12Price:  Number(it.hot12Price)  || 0,
        hot12Cal:    it.hot12Cal,
        hot16Price:  Number(it.hot16Price)  || 0,
        hot16Cal:    it.hot16Cal,
        hot20Price:  Number(it.hot20Price)  || 0,
        hot20Cal:    it.hot20Cal,
        iced16Price: Number(it.iced16Price) || 0,
        iced16Cal:   it.iced16Cal,
        iced20Price: Number(it.iced20Price) || 0,
        iced20Cal:   it.iced20Cal,
      })),
    };
    onSubmit(obj);
  };

  const canSubmit = form.name.trim() && form.items.every(it => it.name.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>
        {editTarget ? '시즌 캠페인 수정' : '시즌 캠페인 추가'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="캠페인 이름 *" name="name"
              value={form.name} onChange={handleField} size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="설명" name="description"
              value={form.description} onChange={handleField}
              size="small" multiline rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="시작일" name="startDate" type="date"
              value={form.startDate} onChange={handleField}
              size="small" InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="종료일" name="endDate" type="date"
              value={form.endDate} onChange={handleField}
              size="small" InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Items section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333' }}>
                메뉴 항목
              </Typography>
              <button
                type="button"
                onClick={addItem}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 12px', border: 'none', borderRadius: 8,
                  background: '#551FFF', color: 'white', fontSize: 12,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                <AddIcon sx={{ fontSize: 14 }} /> 항목 추가
              </button>
            </Box>

            {/* Column header */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              background: '#f5f3ff', borderRadius: '8px 8px 0 0',
              px: 1.5, py: 0.8, mb: 0,
              border: '1px solid #e8e4ff', borderBottom: 'none',
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#551FFF' }}>메뉴명</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{
                  background: '#ff6b35', color: 'white', borderRadius: 1,
                  px: 1.5, py: 0.2, fontSize: 11, fontWeight: 700, mr: 0.5,
                  display: 'flex', alignItems: 'center',
                }}>
                  HOT &nbsp;·&nbsp; 12oz / 16oz / 20oz
                </Box>
                <Box sx={{
                  background: '#0ea5e9', color: 'white', borderRadius: 1,
                  px: 1.5, py: 0.2, fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center',
                }}>
                  ICED &nbsp;·&nbsp; 16oz / 20oz
                </Box>
              </Box>
            </Box>

            {form.items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  border: '1px solid #e8e4ff',
                  borderTop: idx === 0 ? '1px solid #e8e4ff' : 'none',
                  background: 'white',
                  borderRadius: idx === form.items.length - 1 ? '0 0 8px 8px' : 0,
                  px: 1.5, py: 1.2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                  {/* 메뉴명 */}
                  <Box sx={{ flex: '1 1 140px', minWidth: 120 }}>
                    <Typography sx={{ fontSize: 10, color: '#888', mb: 0.3 }}>메뉴명 *</Typography>
                    <input
                      name="name"
                      value={item.name}
                      onChange={e => handleItemField(idx, e)}
                      placeholder="메뉴명"
                      style={{
                        width: '100%', border: '1px solid #e0e0e0', borderRadius: 6,
                        padding: '5px 8px', fontSize: 13, outline: 'none',
                      }}
                    />
                  </Box>

                  {/* HOT */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box sx={{
                      background: '#fff5f0', border: '1px solid #ffcbb3',
                      borderRadius: 1.5, px: 1, py: 0.5,
                      display: 'flex', gap: 0.5, alignItems: 'flex-start',
                    }}>
                      <PriceCell label="HOT 12oz" name="hot12Price" value={item.hot12Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"       name="hot12Cal"   value={item.hot12Cal}   onChange={e => handleItemField(idx, e)} />
                      <Box sx={{ width: '1px', background: '#ffd0b5', alignSelf: 'stretch', mx: 0.3 }} />
                      <PriceCell label="HOT 16oz" name="hot16Price" value={item.hot16Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"       name="hot16Cal"   value={item.hot16Cal}   onChange={e => handleItemField(idx, e)} />
                      <Box sx={{ width: '1px', background: '#ffd0b5', alignSelf: 'stretch', mx: 0.3 }} />
                      <PriceCell label="HOT 20oz" name="hot20Price" value={item.hot20Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"       name="hot20Cal"   value={item.hot20Cal}   onChange={e => handleItemField(idx, e)} />
                    </Box>

                    {/* ICED */}
                    <Box sx={{
                      background: '#f0f9ff', border: '1px solid #bae6fd',
                      borderRadius: 1.5, px: 1, py: 0.5,
                      display: 'flex', gap: 0.5, alignItems: 'flex-start',
                    }}>
                      <PriceCell label="ICED 16oz" name="iced16Price" value={item.iced16Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"        name="iced16Cal"   value={item.iced16Cal}   onChange={e => handleItemField(idx, e)} />
                      <Box sx={{ width: '1px', background: '#bae6fd', alignSelf: 'stretch', mx: 0.3 }} />
                      <PriceCell label="ICED 20oz" name="iced20Price" value={item.iced20Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"        name="iced20Cal"   value={item.iced20Cal}   onChange={e => handleItemField(idx, e)} />
                    </Box>
                  </Box>

                  {/* Remove button */}
                  {form.items.length > 1 && (
                    <IconButton size="small" onClick={() => removeItem(idx)} sx={{ color: '#c62828', mt: 1.8 }}>
                      <RemoveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: '#888' }}>취소</Button>
        <Button
          variant="contained" onClick={handleSubmit} disabled={!canSubmit}
          sx={{ background: '#551FFF', borderRadius: 2 }}
        >
          {editTarget ? '저장' : '추가'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CampaignMenuDialog;
